import {
  Pagination as AntPagination,
  PaginationProps as AntPaginationProps
} from 'antd';
import React from 'react';
import styled, { css } from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography';
import { Select, type SelectOption } from '../Select';

export interface PaginationProps extends Omit<AntPaginationProps, 'total'> {
  total: number;
}

const controlBase = css`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.dime.radius.m};
  border: 1px solid ${theme.dime.colors.border.static};
`;

const disabledControl = css`
  background-color: ${theme.dime.colors.action.primaryDisabled};
  border-color: ${theme.dime.colors.border.static};
`;

const StyledPagination = styled(AntPagination)`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.s};
  color: ${theme.dime.colors.text.primary};
  font-family: ${theme.dime.fonts.families.roboto};
  font-size: ${theme.dime.fonts.sizes.sm};
  line-height: ${theme.dime.fonts.lineHeights.sm};

  .ant-pagination-item {
    ${controlBase}
    min-width: ${theme.dime.spacing.xl};
    height: ${theme.dime.spacing.xl};
    background-color: ${theme.dime.colors.surface.primary};

    .pagination-item-label {
      color: ${theme.dime.colors.text.primary};
    }

    &:hover:not(.ant-pagination-item-active) {
      border-color: ${theme.dime.colors.border.interactiveHover};
    }
  }

  .ant-pagination-item-active {
    border-color: ${theme.dime.colors.border.interactiveSelected};

    .pagination-item-label {
      color: ${theme.dime.colors.text.brandHover};
    }

    &:hover {
      border-color: ${theme.dime.colors.border.interactiveSelected};
    }
  }

  .ant-pagination-prev,
  .ant-pagination-next {
    min-width: ${theme.dime.spacing.xl};
    height: ${theme.dime.spacing.xl};

    .ant-pagination-item-link {
      ${controlBase}
      background-color: ${theme.dime.colors.action.secondary};
      color: ${theme.dime.colors.icon.primary};
    }

    &:hover .ant-pagination-item-link {
      border-color: ${theme.dime.colors.border.interactiveHover};
    }
  }

  .ant-pagination-prev.ant-pagination-disabled,
  .ant-pagination-next.ant-pagination-disabled {
    .ant-pagination-item-link,
    &:hover .ant-pagination-item-link {
      ${disabledControl}
      color: ${theme.dime.colors.icon.disabled};
    }
  }

  .ant-pagination-jump-prev,
  .ant-pagination-jump-next {
    .ant-pagination-item-link-icon {
      color: ${theme.dime.colors.icon.primary};
    }

    .ant-pagination-item-ellipsis {
      color: ${theme.dime.colors.text.secondary};
    }
  }

  .ant-pagination-options {
    margin-inline-start: ${theme.dime.spacing.s};
  }

  && .ant-pagination-options .ant-select {
    width: 120px;

    &.ant-select-single {
      height: ${theme.dime.spacing['2xl']};
    }

    .ant-select-selector {
      height: ${theme.dime.spacing['2xl']};
      min-height: ${theme.dime.spacing['2xl']};
      padding: 0 ${theme.dime.spacing.l};
      border-color: ${theme.dime.colors.border.static};
      background-color: ${theme.dime.colors.fill.primary};

      &:hover {
        border-color: ${theme.dime.colors.border.interactiveHover};
      }

      &:focus-within {
        border-color: ${theme.dime.colors.border.interactiveSelected};
      }
    }

    &.ant-select-open .ant-select-selector {
      border-color: ${theme.dime.colors.border.interactiveSelected};
    }

    .ant-select-selection-item {
      color: ${theme.dime.colors.text.secondary};
      line-height: ${theme.dime.spacing['2xl']};
    }

    .ant-select-arrow {
      color: ${theme.dime.colors.text.secondary};
    }
  }

  .ant-pagination-total-text {
    color: ${theme.dime.colors.text.secondary};
  }

  &.ant-pagination-disabled {
    .ant-pagination-item,
    .ant-pagination-item:hover,
    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-prev:hover .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link,
    .ant-pagination-next:hover .ant-pagination-item-link {
      ${disabledControl}

      .pagination-item-label {
        color: ${theme.dime.colors.text.disabled};
      }
    }

    .ant-pagination-item-active {
      background-color: ${theme.dime.colors.fill.disabled};
    }

    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
      color: ${theme.dime.colors.icon.disabled};
    }

    .ant-pagination-jump-prev .ant-pagination-item-link-icon,
    .ant-pagination-jump-next .ant-pagination-item-link-icon {
      color: ${theme.dime.colors.icon.disabled};
    }
  }
`;

type IconDirection = 'prev' | 'next';

const NavIcon: React.FC<{ direction: IconDirection }> = ({ direction }) => (
  <button type="button" tabIndex={-1} className="ant-pagination-item-link">
    <IconWrap
      type={
        direction === 'prev' ? IconType.ChevronLeft : IconType.ChevronRight
      }
      size={IconSize.Small}
    />
  </button>
);

const JumpIcon: React.FC<{ direction: IconDirection }> = ({ direction }) => (
  <button type="button" tabIndex={-1} className="ant-pagination-item-link">
    <div className="ant-pagination-item-container">
      <IconWrap
        type={
          direction === 'prev' ? IconType.ChevronLeft : IconType.ChevronRight
        }
        size={IconSize.Small}
        className="ant-pagination-item-link-icon"
      />
      <span className="ant-pagination-item-ellipsis">&bull;&bull;&bull;</span>
    </div>
  </button>
);

const prevIcon = <NavIcon direction="prev" />;
const nextIcon = <NavIcon direction="next" />;
const jumpPrevIcon = <JumpIcon direction="prev" />;
const jumpNextIcon = <JumpIcon direction="next" />;

const renderItem: NonNullable<AntPaginationProps['itemRender']> = (
  page,
  type,
  element
) =>
  type === 'page' ? (
    <Typography variant="body1" className="pagination-item-label">
      {page}
    </Typography>
  ) : (
    element
  );

interface PageSizeOptionProps {
  value: number;
}

const PageSizeOption: React.FC<PageSizeOptionProps> = () => null;

type PageSizeSelectProps = {
  value?: number | string;
  disabled?: boolean;
  className?: string;
  onChange?: (value: number) => void;
};

type PageSizeSelectComponent = React.FC<PageSizeSelectProps> & {
  Option: React.FC<PageSizeOptionProps>;
};

const DEFAULT_PAGE_SIZE_OPTIONS = ['10', '20', '50', '100'];

const createPageSizeSelect = (
  pageSizeOptions: Array<string | number> = DEFAULT_PAGE_SIZE_OPTIONS
): PageSizeSelectComponent => {
  const options: SelectOption<number>[] = pageSizeOptions.flatMap((option) => {
    const value = Number(option);
    if (!Number.isFinite(value) || value <= 0) {
      return [];
    }

    return [{ value, label: String(option) }];
  });

  const PageSizeSelect = (({
    value,
    disabled,
    className = '',
    onChange
  }: PageSizeSelectProps) => (
    <Select
      className={`${className} ant-pagination-size-changer`.trim()}
      options={options}
      value={value ? Number(value) : undefined}
      disabled={disabled}
      allowClear={false}
      showSearch={false}
      onChange={(nextValue) => onChange?.(Number(nextValue))}
    />
  )) as PageSizeSelectComponent;

  PageSizeSelect.Option = PageSizeOption;
  return PageSizeSelect;
};

export const Pagination: React.FC<PaginationProps> = ({
  itemRender = renderItem,
  className = '',
  showSizeChanger,
  pageSizeOptions,
  ...props
}) => {
  const selectComponentClass = React.useMemo(
    () => createPageSizeSelect(pageSizeOptions),
    [pageSizeOptions]
  );

  return (
    <StyledPagination
      {...props}
      className={className}
      pageSizeOptions={pageSizeOptions}
      showSizeChanger={!!showSizeChanger}
      selectComponentClass={selectComponentClass}
      itemRender={itemRender}
      prevIcon={prevIcon}
      nextIcon={nextIcon}
      jumpPrevIcon={jumpPrevIcon}
      jumpNextIcon={jumpNextIcon}
    />
  );
};
